import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { formatFreshFoodBoxLine, formatFreshFoodItems } from '../../../../common/utils/pickList/buildFreshFoodByStop';
import Text from '../../../components/text/Text';
import { Card, MachineChip } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useTheme } from '../../../theme';
import { getStopPositionName, getStopSiteName } from '../stopDetails';

/**
 * Fresh food for one stop (`stop` from buildFreshFoodByStop): the supplier
 * boxes the office ticked, then the fresh pick list lines by brand.
 * Props:
 * - stop
 * - docket: names the stop in the header (site, position, machine number), as
 *   in the start of day list; without it the card is titled "Fresh food" (stop screen)
 * - onPress: opens the stop's pick list
 */
const FreshFoodCard = ({ stop, docket, onPress }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const boxLines = stop.boxes.map(formatFreshFoodBoxLine);
	const brandLines = stop.brands.map(brand => ({ key: brand.id || 'other', name: brand.name, items: formatFreshFoodItems(brand) }));
	const siteName = docket ? getStopSiteName(docket) : '';
	const title = docket ? siteName || getStopPositionName(docket) : 'Fresh food';

	return (
		<Card
			tone="raised"
			onPress={onPress}
			accessibilityLabel={[
				docket ? `Fresh food for ${stop.name}` : 'Fresh food',
				...boxLines,
				...brandLines.map(line => `${line.name}: ${line.items}`),
			].join('. ')}
			accessibilityHint={onPress ? 'Opens the pick list for this stop' : undefined}
			style={styles.card}
		>
			<View style={styles.header}>
				<View style={styles.titles}>
					<Text style={styles.title}>{title}</Text>
					{Boolean(docket) && (
						<View style={styles.positionRow}>
							{Boolean(siteName) && <Text style={styles.position}>{getStopPositionName(docket)}</Text>}
							<MachineChip number={docket.machine?.assetId} />
						</View>
					)}
				</View>
				{Boolean(onPress) && <Feather name="chevron-right" size={22} color={colors.iconMuted} />}
			</View>
			{boxLines.map(line => (
				<View key={line} style={styles.line}>
					<Feather name="package" size={18} color={colors.accent} />
					<Text style={styles.boxText}>{line}</Text>
				</View>
			))}
			{brandLines.map(line => (
				<View key={line.key} style={styles.line}>
					<View style={styles.bullet} />
					<Text style={styles.lineText}>
						<Text style={styles.brandName}>{`${line.name}: `}</Text>
						{line.items}
					</Text>
				</View>
			))}
		</Card>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	card: {
		gap: space.sm,
		paddingVertical: 14,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	titles: {
		flex: 1,
		gap: space.xs,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	positionRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	position: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	line: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 10,
	},
	bullet: {
		width: 6,
		height: 6,
		marginTop: 7,
		marginHorizontal: 6,
		borderRadius: radius.pill,
		backgroundColor: colors.accent,
	},
	boxText: {
		...type.bodySmallSemibold,
		color: colors.text,
		flex: 1,
		minWidth: 0,
	},
	// Long product lists wrap inside the card instead of widening it on web.
	lineText: {
		...type.bodySmall,
		color: colors.text,
		flex: 1,
		minWidth: 0,
	},
	brandName: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
});

export default FreshFoodCard;
